import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { SingleDatePicker } from "@/components/shared/SingleDatePicker";

interface EditResultsActionSectionProps {
  outcome?: string | null;
  variantBSwitchDate?: string | null;
  onChange: (outcome: string, switchDate?: string) => void;
}

export function EditResultsActionSection({
  outcome,
  variantBSwitchDate,
  onChange,
}: EditResultsActionSectionProps) {
  const [actionChoice, setActionChoice] = useState<"keep-a" | "apply-b" | null>(
    outcome === "control" ? "keep-a" : outcome === "variant" ? "apply-b" : null,
  );
  const [switchDate, setSwitchDate] = useState<Date | undefined>(
    variantBSwitchDate ? new Date(variantBSwitchDate) : undefined,
  );
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleChooseKeepA = (checked: boolean) => {
    if (!checked) return;
    setActionChoice("keep-a");
    onChange("control");
  };

  const handleChooseApplyB = (checked: boolean) => {
    if (!checked) return;
    setActionChoice("apply-b");
    onChange("variant", switchDate ? switchDate.toISOString() : undefined);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSwitchDate(date);
    setCalendarOpen(false);
    onChange("variant", date ? date.toISOString() : undefined);
  };

  return (
    <div className="space-y-3 bg-muted/50 rounded-md p-3.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Action</p>

      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          checked={actionChoice === "keep-a"}
          onCheckedChange={handleChooseKeepA}
          className="mt-0.5"
        />
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-foreground">Keep Variant A — no listing change</p>
          <p className="text-xs text-muted-foreground">Variant A remains live; no content update submitted.</p>
        </div>
      </label>

      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          checked={actionChoice === "apply-b"}
          onCheckedChange={handleChooseApplyB}
          className="mt-0.5"
        />
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-foreground">Apply Variant B — submit listing change</p>
          <p className="text-xs text-muted-foreground">Variant B content will be submitted as a listing update.</p>
        </div>
      </label>

      {actionChoice === "apply-b" && (
        <div className="pl-7 space-y-2">
          <p className="text-xs text-muted-foreground">Date listing change submitted:</p>
          <SingleDatePicker
            date={switchDate}
            onDateChange={handleDateSelect}
            placeholder="Pick a date"
            className="w-[200px]"
          />
          <p className="text-xs text-muted-foreground italic">
            Signal accounts for the 48–72 hr delay.
          </p>
        </div>
      )}
    </div>
  );
}
